/**
 * Lecteur de leçon partagé (React web), mode « pas à pas » : utilisé par l'app mobile (via un DOM
 * component Expo) et par le studio enseignant (aperçu exact de ce que verra l'élève).
 * Figures : Mafs. Formules : KaTeX. Aucune ressource réseau : fonctionne hors ligne.
 */
import type { LessonContent } from "@jange/shared";
import { BlockView, isInteractive, PLAYER_CSS, useLessonParams, type BlockContext, type PlayerEvent, type ThreeComponents } from "./blocks.js";
import { C } from "./theme.js";

export type { PlayerEvent, ThreeComponents };

export type LessonPlayerProps = {
  lesson: LessonContent & { title: string };
  /** Code de langue (BCP-47) ; repli sur le français pour les titres non traduits. */
  lang: string;
  stepIndex: number;
  onEvent: (e: PlayerEvent) => Promise<void>;
  /** URL d'un fichier (vidéo…) quand il est disponible ; sinon le bloc affiche un résumé. */
  resolveAsset?: (assetId: string) => string | null;
  /** Même chose sous forme de table (les DOM components d'Expo ne reçoivent pas de fonction synchrone). */
  assetUrls?: Record<string, string>;
  /** Charger les modèles 3D sans attendre que l'élève appuie (aperçu du studio, fichier déjà sur le téléphone). */
  autoLoad3d?: boolean;
  three?: ThreeComponents;
};

export function LessonPlayer({ lesson, lang, stepIndex, onEvent, resolveAsset: resolveProp, assetUrls, autoLoad3d = false, three }: LessonPlayerProps) {
  const resolveAsset = (id: string) => assetUrls?.[id] ?? resolveProp?.(id) ?? null;
  const step = lesson.steps[Math.min(stepIndex, lesson.steps.length - 1)];
  const visible = lesson.blocks.filter((b) => step?.blockIds.includes(b.id));
  const { params, setParams, specs, solids } = useLessonParams(lesson.blocks, step?.figureParams);
  const ctx: BlockContext = {
    lang, params, specs, solids, three, resolveAsset, autoLoad3d,
    setParam: (name, v) => setParams((p) => ({ ...p, [name]: v })),
    onEvent: (e) => { void onEvent(e); },
  };

  return (
    <div className="jg-player" style={{ fontFamily: "system-ui, -apple-system, sans-serif", color: C.ink, padding: "4px 16px 16px", display: "flex", flexDirection: "column", gap: 12 }}>
      <style>{PLAYER_CSS}</style>
      <div style={{ display: "flex", gap: 6 }}>
        {lesson.steps.map((s, i) => <div key={s.id} style={{ height: 4, flex: 1, borderRadius: 999, background: i <= stepIndex ? C.teal : C.border, transition: "background-color 300ms ease" }} />)}
      </div>
      <div key={step?.id} className="jg-in" style={{ fontSize: 13, fontWeight: 700, color: C.amberText, background: C.amberTint, padding: "8px 12px", borderRadius: 12 }}>
        Étape {stepIndex + 1} · {step?.title[lang] ?? step?.title.fr}
      </div>
      {/* Texte et questions réapparaissent à chaque étape ; figures et 3D restent montées (transition des curseurs). */}
      {visible.map((b) => <BlockView key={isInteractive(b) || b.type === "formula" ? b.id : `${step?.id}-${b.id}`} block={b} ctx={ctx} animKey={`${step?.id}-${b.id}`} />)}
    </div>
  );
}
